import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types';
import { type RenewalFrequencyValue } from '@/utils/constants';

// One purchase made by a client (ADR-048). The client is permanent; each order
// is an independent sale with its own products, payment, billing and renewal
// dates.
export interface OrderProductLine {
  productCode: string;
  productName: string;
  priceAtSale: number;
  discountAtSale: number;
  termYears: number | null;
  renewalFrequency: RenewalFrequencyValue;
}

export interface OrderRecord {
  id: number;
  leadId: number;
  orderNumber: number;
  orderType: 'Initial' | 'Add-on';
  soldAt: string;
  stepper: string;
  paymentStatus: string;
  paymentAmount: number;
  paymentMode: string | null;
  selectedBankLabel: string | null;
  transactionRef: string | null;
  onboardedAt: string | null;
  createdById: number | null;
  createdByName: string | null;
  products: OrderProductLine[];
}

// A product line the client currently holds, across all their orders. Drives the
// wizard's "already subscribed" panel so an RM can see what not to re-sell.
export interface SubscriptionRecord {
  productCode: string;
  orderId: number;
  orderNumber: number;
  /** Next renewal due date; null until the Service team activates the product. */
  dueDate: string | null;
  cancelled: boolean;
}

/** One installment the client has promised to pay by a date (ADR-053). */
export interface DueInstallmentLine {
  installmentNumber: number;
  amount: number;
  dueDate: string;
  /** True once billing has the row - it does not lock a Due row. */
  handedOver: boolean;
}

// A row in the Sales → Orders table: the order plus the client it belongs to.
export interface OrderListItem extends OrderRecord {
  leadCode: number;
  clientName: string;
  // The firm's everyday name (Fix 1). This list is the CLIENTS view - an
  // onboarded client is filtered out of the leads list - so the columns added
  // there appear here too. Already searchable: the server's free-text filter
  // runs through the same helper the leads list uses.
  brandName: string | null;
  arn: string;
  mobile: string;
  email: string;
  city: string | null;
  // AMFI facts, read-only. `kydCompliant: null` = never fetched, which is not
  // the same as `false`.
  kydCompliant: boolean | null;
  // 'YYYY-MM-DD'.
  arnValidTill: string | null;
  tcStatus: string;
  leadStatus: string;
  inProgress: boolean;
  /** Outstanding Due installments, soonest first; empty when nothing is due. */
  dueInstallments: DueInstallmentLine[];
  nextDueDate: string | null;
  dueAmount: number;
}

/** Correcting one Due installment: move the date, or record the real payment. */
export interface PatchDueInstallmentPayload {
  dueDate?: string;
  mode?: string;
  transactionNo?: string;
}

export interface ListOrdersParams {
  /** Matches the CLIENT (name / ARN / lead code / email). */
  search?: string;
  /** Filters on the ORDER's sale date. */
  from?: string;
  to?: string;
}

/** The plan a client is on, as the upgrade picker needs to describe it. */
export interface UpgradableSubscription {
  orderId: number;
  orderNumber: number;
  productCode: string;
  productName: string;
  tier: number;
  /**
   * False when the Service team has not activated this plan yet. The client has
   * paid but received nothing, so an upgrade now charges the WHOLE setup
   * difference and the superseded plan leaves the service queue instead of
   * being provisioned.
   */
  activated: boolean;
  /** Null until the plan is activated - there is no renewal clock yet. */
  dueDate: string | null;
  frequency: string;
  serviceEnabledAt: string | null;
}

export interface UpgradeTarget {
  productCode: string;
  productName: string;
  tier: number;
}

export interface UpgradeOptions {
  /** null when the client has no live, tiered, activated AUM plan. */
  current: UpgradableSubscription | null;
  targets: UpgradeTarget[];
}

export const ordersService = {
  list: async (params: ListOrdersParams = {}): Promise<OrderListItem[]> => {
    const query: Record<string, string> = {};
    if (params.search) query.search = params.search;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    const { data } = await apiClient.get<ApiResponse<OrderListItem[]>>('/orders', {
      params: Object.keys(query).length ? query : undefined,
    });
    return data.data ?? [];
  },

  /**
   * Corrects one Due installment straight from the Orders queue: move the
   * promised date, or settle it with the payment that arrived (ADR-053).
   * Amounts are untouchable here, so the schedule's total stays valid.
   */
  patchDueInstallment: async (
    orderId: number,
    installmentNumber: number,
    payload: PatchDueInstallmentPayload,
  ): Promise<void> => {
    await apiClient.patch<ApiResponse<unknown>>(
      `/orders/${orderId}/installments/${installmentNumber}`,
      payload,
    );
  },

  getById: async (id: number): Promise<OrderRecord> => {
    const { data } = await apiClient.get<ApiResponse<OrderRecord>>(`/orders/${id}`);
    if (!data.data) throw new Error('Order not found');
    return data.data;
  },

  listForLead: async (leadId: number): Promise<OrderRecord[]> => {
    const { data } = await apiClient.get<ApiResponse<OrderRecord[]>>(`/leads/${leadId}/orders`);
    return data.data ?? [];
  },

  /** Opens a new Add-on order against an existing client. */
  createAddOn: async (leadId: number): Promise<OrderRecord> => {
    const { data } = await apiClient.post<ApiResponse<OrderRecord>>(`/leads/${leadId}/orders`);
    if (!data.data) throw new Error('Could not create the order');
    return data.data;
  },

  listSubscriptions: async (leadId: number): Promise<SubscriptionRecord[]> => {
    const { data } = await apiClient.get<ApiResponse<SubscriptionRecord[]>>(
      `/leads/${leadId}/subscriptions`,
    );
    return data.data ?? [];
  },

  /**
   * What this client could move up to: their current AUM plan (null when they
   * have none eligible) and the higher tiers available.
   */
  upgradeOptions: async (leadId: number): Promise<UpgradeOptions> => {
    const { data } = await apiClient.get<ApiResponse<UpgradeOptions>>(
      `/leads/${leadId}/upgrade-options`,
    );
    return data.data ?? { current: null, targets: [] };
  },

  /** Opens an Upgrade order moving the client's live plan to a higher tier. */
  createUpgrade: async (leadId: number, targetProductCode: string): Promise<OrderRecord> => {
    const { data } = await apiClient.post<ApiResponse<OrderRecord>>(
      `/leads/${leadId}/upgrade-orders`,
      { targetProductCode },
    );
    if (!data.data) throw new Error('Could not create the upgrade order');
    return data.data;
  },
};
